// src/features/inventory/pages/BatchDetailPage/BatchHeader.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  CompareArrows as TransferIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../../../hooks/api/useApi';
import { lotsAPI } from '../../services/lots';
import { itemsAPI } from '../../services/items';
import { usePermissions } from '../../../auth/hooks/usePermissions';
import RecordTransactionDialog from '../ItemDetailPage/RecordTransactionDialog';
import TransferStockDialog from '../ItemDetailPage/TransferStockDialog';
import { LOT_STATUS_COLOR, LOT_STATUSES } from '../../constants/lotStatuses';

import PageHeader from '../../../../components/common/PageHeader';

function UpdateStatusDialog({ open, onClose, lot, onSuccess }) {
  const { t } = useTranslation(['inventory', 'common']);
  const { loading, error, execute, clearError } = useApi();
  const [status, setStatus] = useState(lot?.status?.toLowerCase() ?? 'active');
  const [notes, setNotes] = useState('');

  const handleClose = () => {
    clearError();
    setNotes('');
    onClose();
  };

  const handleSubmit = async () => {
    const result = await execute(() =>
      lotsAPI.updateStatus(lot.lot_id, { status, notes: notes.trim() || null })
    );
    if (result.success) {
      onSuccess();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inventory:batches.update_status.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && <Alert severity="error" onClose={clearError}>{error}</Alert>}
          <TextField
            select
            label={t('inventory:batches.grid.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            required
            disabled={loading}
          >
            {LOT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`inventory:batches.filters.${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('common:labels.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            disabled={loading}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t('common:buttons.saving') : t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BatchHeader({ lot, canUpdate, canRecord, onBack, onStatusUpdated }) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const { hasScope } = usePermissions();
  const canReadItem = hasScope('inventory:item:read');
  const { execute } = useApi();
  const [item, setItem] = useState(null);

  // Name the item the batch belongs to; the lot itself only carries its id
  useEffect(() => {
    if (!canReadItem) return undefined;
    let cancelled = false;
    execute(() => itemsAPI.getItemById(lot.item_id), { showError: false }).then((result) => {
      if (!cancelled && result.success) setItem(result.data.data);
    });
    return () => { cancelled = true; };
  }, [canReadItem, lot.item_id, execute]);

  const status = lot.status?.toLowerCase();

  return (
    <>
      <PageHeader
        onBack={onBack}
        title={<Box component="span" sx={{ fontFamily: 'monospace' }}>{lot.lot_number}</Box>}
        crumb={lot.lot_number}
        badges={<>
          <Chip
            label={t(`inventory:batches.filters.${status}`)}
            color={LOT_STATUS_COLOR[status] || 'default'}
            size="small"
            variant="outlined"
            onClick={canUpdate ? () => setStatusDialogOpen(true) : undefined}
            onDelete={canUpdate ? () => setStatusDialogOpen(true) : undefined}
            deleteIcon={canUpdate ? <EditIcon titleAccess={t('inventory:batches.detail.update_status')} /> : undefined}
          />
        </>}
        subtitle={canReadItem && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {t('inventory:batches.grid.item')}:
            <Button
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem !important' }} />}
              onClick={() => navigate(`/inventory/items/${lot.item_id}`)}
              sx={{ minWidth: 0, p: 0.25, textTransform: 'none', fontSize: '0.875rem', lineHeight: 1.5 }}
            >
              {item ? `${item.name} (${item.sku})` : t('inventory:batches.detail.item_link')}
            </Button>
          </Box>
        )}
        actions={canRecord && (
          <>
            <Button
              variant="outlined"
              startIcon={<TransferIcon />}
              onClick={() => setTransferOpen(true)}
            >
              {t('inventory:items.transfer_stock.button')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAdjustOpen(true)}
            >
              {t('inventory:batches.detail.record_transaction')}
            </Button>
          </>
        )}
      />

      {canUpdate && (
        <UpdateStatusDialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          lot={lot}
          onSuccess={onStatusUpdated}
        />
      )}

      {canRecord && (
        <RecordTransactionDialog
          open={adjustOpen}
          onClose={() => setAdjustOpen(false)}
          onSuccess={() => {
            setAdjustOpen(false);
            onStatusUpdated();
          }}
          lot={lot}
          itemId={lot.item_id}
          uom={item?.uom}
        />
      )}

      {canRecord && (
        <TransferStockDialog
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          onSuccess={onStatusUpdated}
          itemId={lot.item_id}
          uom={item?.uom}
          preselectedLot={lot}
        />
      )}
    </>
  );
}
